import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import Assignment from '@mui/icons-material/Assignment';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { DataGrid } from 'react-data-grid';
import ReactHtmlParser from 'react-html-parser';
import { toast } from 'react-toastify';

import DefaultApi from '../../Api/DefaultApi';
import ImageUploadEditor from '../../Components/Editors/ImageUploadEditor';
import DraftEditor from '../../Components/Editors/DraftEditor';
import {
  FilterContext,
  rowKeyGetter,
  FilterRenderer,
  inputStopPropagation,
  autoFocusAndSelect,
  handleCopy,
  renderTextEditor,
} from '../../Components/ReactDataGridProps';
import { creators as CompanySettingsActions } from '../../Reducers/CompanySettings';
import appdata from '../../Services/appdata';

import OrgTable from './OrgTable';
import RestApiGrid from './RestApiGrid';
import './style.css';

function getComparator(sortColumn) {
  switch (sortColumn) {
    case 'FiscalYearStartMonth':
    case 'SafetyBankBalance':
      return (a, b) => {
        return a[sortColumn] - b[sortColumn];
      };
    case 'CompanyCode':
    case 'CompanyName':
    case 'MissionStatement':
      return (a, b) => {
        return a[sortColumn]?.localeCompare(b[sortColumn]);
      };
    default:
      throw new Error(`unsupported sortColumn: "${sortColumn}"`);
  }
}

function CompanySettings() {
  const dispatch = useDispatch();
  const companySettings = useSelector(
    (state) => state.CompanySettings.company_settings,
  );
  const [comRows, setComRows] = useState([]);
  const [comFilters, setComFilters] = useState({
    enabled: false,
  });
  const [comSortColumns, setComSortColumns] = useState([]);
  const [selectedComRow, setSelectedComRow] = useState(null);
  const [userPackage, setUserPackage] = useState({});
  const [slug, setSlug] = useState('');
  const [synergyCompany, setSynergyCompany] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('');

  useEffect(() => {
    dispatch(CompanySettingsActions.companySettingsGetAllCompanySettings());
    setUserPackage(appdata.getItem('package'));

    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get('error');
    if (oauthError) {
      const messageMap = {
        invalid_state: 'OAuth failed: missing provider state.',
        session_expired: 'OAuth failed: the connection session expired. Please try Connect again.',
        auth_denied: 'OAuth was cancelled or denied.',
        no_code: 'OAuth failed: the provider did not return an authorization code.',
        token_exchange_failed: 'OAuth failed while exchanging the authorization code.',
        token_storage_failed: 'OAuth connected but token storage failed.',
        tenant_not_detected: 'OAuth connected but the Xero organisation could not be detected.',
        tenant_persist_failed: 'OAuth connected but the Xero organisation could not be saved.',
        connection_failed: 'OAuth connection failed.',
      };
      toast.error(messageMap[oauthError] || `OAuth connection failed: ${oauthError}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    if (appdata.getLocalItem('synergy_company')) {
      setModalOpen(true);
      setModalMode('slug');
      setSynergyCompany(parseInt(appdata.getLocalItem('synergy_company')));
      localStorage.removeItem('synergy_company');
    }
  }, [dispatch]);

  useEffect(() => {
    if (companySettings) setComRows(companySettings);
  }, [companySettings]);

  const handleComCellClick = (rowData) => {
    if (selectedComRow?.id !== rowData.row.id) {
      setSelectedComRow(rowData.row);
      setSynergyCompany(rowData.row.id);
    }
  };

  const handlePaste = ({
    sourceColumnKey,
    sourceRow,
    targetColumnKey,
    targetRow,
  }) => {
    const incompatibleColumns = ['id', 'ImageData', 'IsActive'];
    if (
      incompatibleColumns.includes(targetColumnKey) ||
      incompatibleColumns.includes(sourceColumnKey)
    ) {
      return targetRow;
    }

    return { ...targetRow, [targetColumnKey]: sourceRow[sourceColumnKey] };
  };

  const onComClickedAdd = () => {
    DefaultApi.addCompanySetting().then((res) => {
      if (res.status === 200) {
        dispatch(CompanySettingsActions.companySettingsGetAllCompanySettings());
      } else toast.error(res.message);
    }).catch((error) => toast.error(error.message));
  };

  const handleComRowChange = (updatedRows, field) => {
    setComRows(updatedRows);
    let updated_data = {
      rowIds: [updatedRows[field.indexes[0]].id],
      updated: {
        [field.column.key]: updatedRows[field.indexes[0]][field.column.key],
      },
    };
    
    DefaultApi.updateCompanySettingsData(updated_data).then((res) => {
      if (res.status === 200) {
        dispatch(CompanySettingsActions.companySettingsGetAllCompanySettings());
      } else toast.error(res.message);
    }).catch((error) => toast.error(error.message));
  };

  const onComClickedDelete = async () => {
    if (!selectedComRow) return;

    await DefaultApi
      .deleteCompanySetting(selectedComRow.id)
      .then((res) => {
        if (res.status === 200) {
          dispatch(
            CompanySettingsActions.companySettingsGetAllCompanySettings(),
          );
          setSelectedComRow(null);
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const onPackageView = () => {
    setModalOpen(true);
    setModalMode('package');
  };

  const comToggleFilters = () => {
    setComFilters((filters) => ({
      ...filters,
      enabled: !filters.enabled,
    }));
  };

  const handlePackageModalPayment = () => {};

  const handleChangeSlug = (event) => {
    setSlug(event.target.value);
  };

  const handleModalClose = async () => {
    if (slug) {
      await DefaultApi
        .updateCompanyApiKey({
          companyKey: synergyCompany,
          slug,
        })
        .then((res) => {
          setSynergyCompany(null);
        })
        .catch((error) => toast.error(error.message));
    }

    setModalOpen(false);
    setModalMode('');
  };

  const comColumns = useMemo(() => {
    return [
      {
        key: 'id',
        name: 'ID',
        sortable: true,
        resizable: true,
        width: 40,
      },
      {
        key: 'CompanyCode',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>Company Code</b>
            {comSortColumns.find((column) => column.columnKey === 'CompanyCode')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : comSortColumns.find(
                (column) => column.columnKey === 'CompanyCode',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 115,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.CompanyCode}
                onChange={(e) =>
                  setComFilters({
                    ...comFilters,
                    CompanyCode: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        renderEditCell: renderTextEditor,
      },
      {
        key: 'CompanyName',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>Company Name</b>
            {comSortColumns.find((column) => column.columnKey === 'CompanyName')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : comSortColumns.find(
                (column) => column.columnKey === 'CompanyName',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 115,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.CompanyName}
                onChange={(e) =>
                  setComFilters({
                    ...comFilters,
                    CompanyName: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        renderEditCell: renderTextEditor,
      },
      {
        key: 'FiscalYearStartMonth',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>FiscalYearStartMonth</b>
            {comSortColumns.find(
              (column) => column.columnKey === 'FiscalYearStartMonth',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : comSortColumns.find(
                (column) => column.columnKey === 'FiscalYearStartMonth',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 150,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.FiscalYearStartMonth}
                onChange={(e) =>
                  setComFilters({
                    ...comFilters,
                    FiscalYearStartMonth: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.FiscalYearStartMonth}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                FiscalYearStartMonth: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'SafetyBankBalance',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>SafetyBankBalance</b>
            {comSortColumns.find(
              (column) => column.columnKey === 'SafetyBankBalance',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : comSortColumns.find(
                (column) => column.columnKey === 'SafetyBankBalance',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 135,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.SafetyBankBalance}
                onChange={(e) =>
                  setComFilters({
                    ...comFilters,
                    SafetyBankBalance: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.SafetyBankBalance}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                SafetyBankBalance: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'ImageData',
        name: 'Logo',
        width: 73,
        resizable: true,
        renderEditCell: (props) => (
          <ImageUploadEditor
            rowData={props.row}
            uploadImage={(value) =>
              props.onRowChange({ ...props.row, ImageData: value }, true)
            }
          />
        ),
        renderCell: (props) =>
          props.row.ImagePath ? (
            <img
              src={`data:image/${
                props.row.ImagePath.split('.')[
                  props.row.ImagePath.split('.').length - 1
                ]
              };base64,${props.row.ImageData}`}
              alt="company_logo"
              style={{ maxWidth: 56, maxHeight: 60 }}
            />
          ) : null,
      },
      {
        key: 'MissionStatement',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>Mission Statement</b>
            {comSortColumns.find(
              (column) => column.columnKey === 'MissionStatement',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : comSortColumns.find(
                (column) => column.columnKey === 'MissionStatement',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 135,
        sortable: true,
        filterable: true,
        resizable: true,
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.MissionStatement}
                onChange={(e) =>
                  setComFilters({
                    ...comFilters,
                    MissionStatement: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        renderEditCell: (props) => (
          <DraftEditor
            keyValue="MissionStatement"
            value={props.row.MissionStatement}
            onCommit={(value) => {
              props.onRowChange(
                { ...props.row, MissionStatement: value },
                true,
              );
            }}
          />
        ),
        renderCell: (props) => {
          return (
            <div className="htmlParser">
              {ReactHtmlParser(props.row.MissionStatement)}
            </div>
          );
        },
      },
      {
        key: 'IsActive',
        name: 'Active',
        renderCell: (props) => (
          <Checkbox
            checked={props.row.IsActive !== null ? props.row.IsActive : false}
            onChange={(e) => {
              e.preventDefault();
              props.onRowChange(
                { ...props.row, IsActive: !props.row.IsActive },
                true,
              );
            }}
            color="success"
          ></Checkbox>
        ),
      },
    ];
  }, [comFilters, comSortColumns]);

  const filteredRows = useMemo(() => {
    return comRows?.filter((r) => {
      return (
        (comFilters.CompanyCode
          ? r.CompanyCode?.toLowerCase().includes(
              comFilters.CompanyCode.toLowerCase(),
            )
          : true) &&
        (comFilters.CompanyName
          ? r.CompanyName?.toLowerCase().includes(
              comFilters.CompanyName.toLowerCase(),
            )
          : true) &&
        (comFilters.FiscalYearStartMonth
          ? r.FiscalYearStartMonth?.toString().includes(
              comFilters.FiscalYearStartMonth.toString(),
            )
          : true) &&
        (comFilters.SafetyBankBalance
          ? r.SafetyBankBalance?.toString().includes(
              comFilters.SafetyBankBalance.toString(),
            )
          : true) &&
        (comFilters.MissionStatement
          ? r.MissionStatement?.toLowerCase().includes(
              comFilters.MissionStatement.toLowerCase(),
            )
          : true)
      );
    });
  }, [comRows, comFilters]);

  const sortedRows = useMemo(() => {
    if (comSortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of comSortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, comSortColumns]);
  
  return (
    <div className="CompanySettings">
      <Grid container columns={19} spacing={1} sx={{ height: '50%' }}>
        <Grid size={9}>
          <div style={{ display: 'flex', width: '100%' }}>
            <div style={{ flex: 1 }} />
            <IconButton aria-label="Add" onClick={onComClickedAdd}>
              <AddIcon />
            </IconButton>
            <IconButton aria-label="Search" onClick={comToggleFilters}>
              <SearchIcon />
            </IconButton>
            <IconButton aria-label="Delete" onClick={onComClickedDelete}>
              <DeleteIcon />
            </IconButton>
            <IconButton aria-label="Assignment" onClick={onPackageView}>
              <Assignment />
            </IconButton>
          </div>
          <FilterContext.Provider value={comFilters}>
            <DataGrid
              rowKeyGetter={rowKeyGetter}
              className={
                comFilters.enabled
                  ? 'filter-container fill-grid rdg-light'
                  : 'fill-grid rdg-light'
              }
              columns={comColumns}
              rows={sortedRows}
              headerRowHeight={comFilters.enabled ? 70 : 40}
              rowClass={(row, index) =>
                index % 2 === 1 ? 'rdg-highlight-cell' : undefined
              }
              sortColumns={comSortColumns}
              onSortColumnsChange={setComSortColumns}
              onRowsChange={handleComRowChange}
              onCellClick={handleComCellClick}
              onCopy={handleCopy}
              onPaste={handlePaste}
              rowHeight={60}
              style={{ height: window.innerHeight / 2 - 100 }}
            />
          </FilterContext.Provider>
        </Grid>
        <Grid size={10}>
          {selectedComRow && <OrgTable comRow={selectedComRow} />}
        </Grid>
      </Grid>

      {selectedComRow && (
        <RestApiGrid
          comRow={selectedComRow}
          setModalMode={setModalMode}
          setModalOpen={setModalOpen}
          setSlug={setSlug}
        />
      )}

      <Dialog
        fullWidth={true}
        maxWidth="xs"
        open={modalOpen && modalMode === 'package'}
        onClose={handleModalClose}
        aria-labelledby="max-width-dialog-title"
      >
        <DialogTitle id="max-width-dialog-title">Your Package</DialogTitle>
        <DialogContent>
          <DialogContentText>You can upgrade your package anytime.</DialogContentText>
          <Grid container justifyContent="center">
            <Grid size={10} p={2}>
              <Paper justifyContent="center" className="package_item">
                <h3 className="package_name">{userPackage.name}</h3>
                <p className="price">
                  {userPackage.amount_usd === 0
                    ? 'Free'
                    : '$ ' + userPackage.amount_usd}
                </p>
                <p className="month">per month</p>
                <ul className="detail">
                  <li>Data entry1</li>
                  <li>Data entry2</li>
                </ul>
              </Paper>
            </Grid>
          </Grid>
          <Grid container justifyContent="center">
            <Button
              onClick={handlePackageModalPayment}
              variant="outlined"
              color="success"
              style={{
                marginRight: 8,
              }}
            >
              Pay now
            </Button>
            <Button onClick={handleModalClose} variant="outlined" color="error">
              Close
            </Button>
          </Grid>
        </DialogContent>
      </Dialog>

      <Dialog
        fullWidth={true}
        maxWidth="sm"
        open={modalOpen && modalMode === 'slug'}
        onClose={handleModalClose}
      >
        <DialogContent>
          <DialogContentText>Please input slug!</DialogContentText>
          <Grid container>
            <Grid size={12}>
              <TextField
                id="slug"
                label="Slug"
                name="slug"
                value={slug}
                onChange={handleChangeSlug}
                variant="filled"
                fullWidth
              />
              <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                <Button onClick={handleModalClose} variant="outlined" color="error">
                  Close
                </Button>
                <Button
                  variant="outlined"
                  color="success"
                  onClick={handleModalClose}
                >
                  Save
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CompanySettings;
